/**
 * This file is part of vue-boilerplate.
 * @link     : https://zhaiyiming.com/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 TINYMINS.
 */

import get from 'lodash/get';
import { wechat } from 'vue-wechat/1.6.0';
import { ICON_URL } from '@/config';
import store from '@/store';
import { COMMON } from '@/store/types';
import { showDialog } from '@/store/utils';
import { concatPath, safeCall } from '@/utils/util';
import { routeClone, routeEquals, RouteInfo } from '@/utils/navigation';
import { isLocalhost, isInDevMode, isInWechat, isInWechatMobile, isIniOS } from './environment';

export const onWechatSDKFail = (...args): void => {
  if (!isInDevMode('manually')) {
    return;
  }
  console.error(...args);
};

export interface ShareData {
  title?: string;
  desc?: string;
  link?: string;
  route?: RouteInfo;
  imgUrl?: string;
  overwrite?: boolean;
  success?: () => void;
  cancel?: () => void;
}

interface WechatShareMenu {
  hideMenuItems?: (config: { menuList: string[]; fail?: (...args) => void }) => void;
}

const WECHAT_SHARE_ENABLED = false;
const WECHAT_HIDE_MENU_ITEMS_API = 'hideMenuItems';
const WECHAT_SHARE_MENU_LIST = [
  'menuItem:share:appMessage',
  'menuItem:share:timeline',
  'menuItem:share:qq',
  'menuItem:share:QZone',
  'menuItem:share:weiboApp',
  'menuItem:share:facebook',
];

let shareData, shareReady;

const getWechatSDKApiList = (jsApiList: string[] = []): string[] => {
  if (WECHAT_SHARE_ENABLED || jsApiList.indexOf(WECHAT_HIDE_MENU_ITEMS_API) >= 0) {
    return jsApiList;
  }
  return jsApiList.concat(WECHAT_HIDE_MENU_ITEMS_API);
};

const hideWechatShareMenu = (): void => {
  const wx = wechat as typeof wechat & WechatShareMenu;
  if (wx.hideMenuItems) {
    wx.hideMenuItems({
      menuList: WECHAT_SHARE_MENU_LIST,
      fail: onWechatSDKFail,
    });
  }
};

const applyPageShare = (data): void => {
  if (!WECHAT_SHARE_ENABLED) {
    shareData = void 0;
    return;
  }
  if (!shareReady) {
    shareData = data;
  } else if (data) {
    const { title, desc, link, imgUrl, success, cancel } = data;
    if (isInWechatMobile()) {
      wechat.updateTimelineShareData({ title: `${title} ${desc}`, link, imgUrl, success, cancel, fail: onWechatSDKFail });
      wechat.updateAppMessageShareData({ title, desc, link, imgUrl, success, cancel, fail: onWechatSDKFail });
    } else {
      wechat.onMenuShareTimeline({ title: `${title} ${desc}`, link, imgUrl, success, cancel, fail: onWechatSDKFail });
      wechat.onMenuShareAppMessage({ title, desc, link, imgUrl, type: 'link', dataUrl: '', success, cancel, fail: onWechatSDKFail });
      wechat.onMenuShareQQ({ title, desc, link, imgUrl, success, cancel, fail: onWechatSDKFail });
      wechat.onMenuShareQZone({ title, desc, link, imgUrl, success, cancel, fail: onWechatSDKFail });
    }
  }
};
const onShareReady = (): void => {
  shareReady = true;
  if (!WECHAT_SHARE_ENABLED) {
    shareData = void 0;
    return;
  }
  if (shareData) {
    applyPageShare(shareData);
    shareData = void 0;
  }
};

/**
 * 设置页面分享信息
 * @param {object} config 分享信息配置表
 * @returns {void}
 */
export const setPageShare = ({
  title = document.title,
  desc = '',
  link = '',
  route = store.state.common.route.current || store.state.common.route.to,
  imgUrl = ICON_URL,
  overwrite = true,
  success = () => { },
  cancel = () => { },
}: ShareData = { overwrite: false }): void => {
  if (!WECHAT_SHARE_ENABLED) {
    shareData = void 0;
    return;
  }
  if (!route) {
    return;
  }
  if (!overwrite && shareData && routeEquals(route, shareData.route)) {
    return;
  }
  if (!link) {
    link = concatPath(window.location.origin, process.env.PUBLIC_PATH, route.fullPath);
  }
  const user = store.getters['user/user'];
  if (user) {
    link += link.indexOf('?') > 0 ? '&' : '?';
    link += `__from_uid=${user.id.toString(16)}`;
  }
  applyPageShare({ title, desc, link, imgUrl, route: routeClone(route), success, cancel });
};

export const setPageTitle = (title: string): void => {
  if (!title) {
    title = '\u200e';
  }
  document.title = title;
  // 在微信iOS webview更新到WKWebView之前我们可以通过加载一个iframe来实现单页面应用title更改。但是17年初更新到WKWebView后该方法也失效，
  // 据对开发者十分特别不友好的把所有文档放在同一个页面不能通过url区分甚至连锚点也懒得做的的微信开发文档说，3月份会修复。
  // const mobile = navigator.userAgent.toLowerCase();
  // if (/iphone|ipad|ipod/.test(mobile)) {
  //   const iframe = document.createElement('iframe');
  //   iframe.style.visibility = 'hidden';
  //   iframe.setAttribute('src', '/favicon.ico');
  //   const iframeCallback = () => {
  //     setTimeout(() => {
  //       iframe.removeEventListener('load', iframeCallback);
  //       document.body.removeChild(iframe);
  //     }, 0);
  //   };
  //   iframe.addEventListener('load', iframeCallback);
  //   document.body.appendChild(iframe);
  // }
};

/**
 * 根据当前 URL 配置微信 SDK 参数
 * @returns {Promise} Promise
 */
export const configWechatSDK = (() => {
  if (!isInWechat()) {
    return () => new Promise(() => { });
  }
  let currentLocation = '';
  let currentReady = false;
  let readyResolves: Function[] = [];
  let readyRejects: Function[] = [];
  const clearReadyPromise = (): void => {
    readyResolves = [];
    readyRejects = [];
  };
  const onResolve = (): void => {
    // console.error('wx.ready()');
    currentReady = true;
    readyResolves.forEach(cb => safeCall(cb));
    if (!WECHAT_SHARE_ENABLED) {
      hideWechatShareMenu();
    }
    clearReadyPromise();
  };
  const onReject = (err): void => {
    // console.error('wx.error()', err);
    readyRejects.forEach(cb => safeCall(cb, err));
    clearReadyPromise();
  };
  // init wechat SDK
  if (typeof window.WeixinJSBridge === 'undefined' && isInWechatMobile() && isIniOS()) {
    setTimeout(() => {
      if (typeof window.WeixinJSBridge === 'undefined') {
        const confirm = get(window, 'confirm');
        if (confirm('微信环境异常，是否刷新重试？')) {
          window.location.reload();
        }
      }
    }, 500);
  }
  readyResolves.push(onShareReady);
  wechat.error((e) => {
    onReject(e);
    showDialog({
      title: 'Wechat SDK error',
      content: e.errMsg,
    });
    // if (e.errMsg === 'config:invalid signature' && !isInDevMode()) {
    //   window.location.reload();
    // }
  });
  // return Promise
  return (): Promise<void> => {
    let useEntry = isInWechatMobile();
    if (useEntry && !isIniOS()) {
      const route = store.state.common.route.current;
      if (route && (route.name === 'secret_detail' || route.name === 'secret_create')) {
        useEntry = false;
      }
    }
    const location = useEntry ? window.entryLocation : window.location.href.split('#')[0];
    if (!isLocalhost()) {
      if (location !== currentLocation) {
        currentLocation = location;
        currentReady = false;
        store.dispatch(`common/${COMMON.GET_WECHAT_SDK_INFO}`, { url: location }).then((info) => {
          if (location === currentLocation) {
            wechat.ready(onResolve);
            wechat.config({
              debug: info.debug,
              appId: info.appId,
              timestamp: info.timestamp,
              nonceStr: info.nonceStr,
              signature: info.signature,
              jsApiList: getWechatSDKApiList(info.jsApiList),
              fail: onWechatSDKFail,
            });
            // console.log('wx.config()', info);
          }
        }).catch(onReject);
      }
      // if not ready
      if (!currentReady) {
        return new Promise((resolve, reject) => {
          readyResolves.push(resolve);
          readyRejects.push(reject);
        });
      }
    }
    return Promise.resolve();
  };
})();

export const checkWepayReqirement = (): boolean =>
  // if (isInWechatMobile()) {
  //  const currentPath = window.location.pathname.replace(/(^\/+|\/+$)/uig, '');
  //  const expectPath = (process.env.PUBLIC_PATH || '').replace(/(^\/+|\/+$)/uig, '');
  //  if (currentPath !== expectPath) {
  //    window.location.replace(`${process.env.PUBLIC_PATH}?_=${new Date().valueOf()}${window.location.hash}`);
  //    return false;
  //  }
  // }
  true;
